import { Button, Card, Col, Collapse, DateField, Grid, Icons, Row, Space, Tag, Tooltip, Typography } from '@pankod/refine-antd'
import { useNavigation } from '@pankod/refine-core'
import { definitions } from 'interfaces'
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { currency, getGender, getLastOrder } from 'utility'
import {
  ADMITTED_STATUS,
  API_URL,
  CANCELLED_STATUS,
  DEFAULT_DATE_FORMAT,
  DEFAULT_DATE_TIME_FORMAT,
  DELIVERED_STATUS,
  ECONOMY_SHIPMENT_METHOD_ID,
  NO_STOCK_STATUS,
  ORDER_STATUS_ID_ON_HOLD,
  ORDER_STATUS_ID_PENDING_PAYMENT,
  ORDER_STATUS_ID_REQUESTED,
  PARCIAL_RETURN_STATUS,
  PENALIZATION_STATUS,
  PREPARED_STATUS,
  PROCESSED_STATUS,
  RETURN_STATUS,
  SUPPLEMENT_STATUS,
  TIPSA_AGENCY_CODE,
  TIPSA_AGENCY_SERVICE_TYPE_CONSOLIDATED,
  TIPSA_AGENCY_SERVICE_TYPE_DIRECT
} from 'utility/constants'
import { checkIfInvoiceIsEditable, checkStorageInvoice, getRefundInvoicesByOrderId, getShipmentData } from 'utility/supabaseApi'
import { AddressInfo } from './address_info'

const { useBreakpoint } = Grid

type UserAddressesProps = {
  order: definitions['Order'] | undefined;
  canEdit?: boolean
  comesFromIssue?: boolean
  warehouseMode?: boolean
};

export const getInternalShipmentTotal = (userOrders: any) => {
  if (!userOrders) return 0

  const total = userOrders.filter((o: any) => o.status_id && ![ORDER_STATUS_ID_PENDING_PAYMENT, CANCELLED_STATUS, RETURN_STATUS].includes(o.status_id)).reduce((acc: any, curr: any) => {
    const { shipping_price: shippingPrice, marketplace_id: marketplaceId, syniva_price: synivaPrice, items } = curr

    let shipmentCost = 0
    if (marketplaceId.id !== 0) {
      if (synivaPrice > (marketplaceId.free_shipment_price || 0)) {
        shipmentCost = 0
      } else {
        shipmentCost = marketplaceId.shipment_price || 0
      }
    } else {
      if (items && items.length > 0) {
        shipmentCost = items?.reduce((prev: any, curr: any) => (prev.shipping_price_to_madrid || 0) > (curr.shipping_price_to_madrid || 0) ? prev : curr)?.shipping_price_to_madrid || 0
      }
    }

    return acc + (shippingPrice - shipmentCost)
  }, 0)

  return total
}

export const UserAddresses: React.FC<UserAddressesProps> = (props) => {
  const { t } = useTranslation()
  const { xl } = useBreakpoint()
  const { show } = useNavigation()

  const [order, setOrder] = useState(props?.order)
  const [modifiedAddress, setModifiedAddress] = useState(Date.now)
  const [refundInvoices, setRefundInvoices] = useState<definitions['refunds_invoices'][]>([])
  const [canGenerateInvoice, setCanGenerateInvoice] = useState<boolean>(false)
  const [invoiceWarning, setInvoiceWarning] = useState<boolean>(false)

  // If this condition changes, do it on the backend too. except (!order?.invoices?.invoice_number) because it's for showing the button text, not for allowing or not to generate the invoice
  const doNotViewDraft = (order && order.status_id && (order.status_id >= ADMITTED_STATUS || order.status_id === RETURN_STATUS) && ![CANCELLED_STATUS, NO_STOCK_STATUS].includes(order.status_id)) || (order?.invoices?.invoice_number)

  const invoiceButtonAction = useMemo(() => {
    if (doNotViewDraft) {
      return t('buttons.viewInvoice')
    }

    return t('buttons.viewDraft')
  }, [order, doNotViewDraft])

  const [shipmentData, setShipmentData] = useState({
    delivery_note: '',
    tracking_url: null,
    tracking_id: null,
    shipper_type: '',
    type: '',
    id: null
  })

  const shipmentLink = useRef(null)

  // A shipment is written by the preparation flow after this card has already
  // mounted.  Keep the local editable address view aligned with the refreshed
  // order so the tracking URL appears without a manual browser refresh.
  useEffect(() => {
    setOrder(props.order)
  }, [props.order])

  const retrieveRefundInvoices = async () => {
    const data = await getRefundInvoicesByOrderId(order?.id)
    if (data) {
      setRefundInvoices(data)
    }
  }

  const retrieveInvoiceInfo = async () => {
    // Check if the invoice is editable before making changes
    const { editable } = await checkIfInvoiceIsEditable(order?.id)

    const orderInvoiceExists = !!order?.invoices?.invoice_number

    let data: any[] = []

    if (order?.invoices?.invoice_number) {
      const { data: invoiceData } = await checkStorageInvoice(order?.invoices?.invoice_number!)
      data = invoiceData || []
    }

    if (orderInvoiceExists && data?.length === 0) {
      setCanGenerateInvoice(true)
      setInvoiceWarning(true)
    } else if (!editable && data?.length > 0) {
      setCanGenerateInvoice(false)
    } else {
      setCanGenerateInvoice(true)
    }
    console.log({ editable, data })
  }

  useEffect(() => {
    retrieveInvoiceInfo()
    retrieveRefundInvoices()
  }, [])

  const retrieveShipment = async () => {
    setShipmentData(await getShipmentData(order?.id || null))
  }

  const openLink = (url: string) => {
    window.open(url, '_blank')
  }

  useEffect(() => {
    retrieveShipment()
  }, [order?.id, order?.shipments?.map((shipment) => shipment.tracking_id || '').join('|')])

  const showShipmentData = () => {
    if (shipmentData?.tracking_url) {
      if (shipmentData?.shipper_type?.toLowerCase().includes('tipsa')) {
        return <>
          <Button ref={shipmentLink} icon={<Icons.InfoCircleOutlined />} onClick={() => openLink(shipmentData.tracking_url || '')}>
            {t('order.fields.shipmentDataAvailable')}
          </Button>
          {shipmentData?.delivery_note && <Typography.Text>
            <strong>{t('order.fields.shipmentDataAvailable')}: </strong> {TIPSA_AGENCY_CODE}{(shipmentData?.type.toLowerCase().includes('directo') ? TIPSA_AGENCY_SERVICE_TYPE_DIRECT : TIPSA_AGENCY_SERVICE_TYPE_CONSOLIDATED)}{shipmentData?.delivery_note}
          </Typography.Text>}
        </>
      } else if (shipmentData?.shipper_type?.toLowerCase().includes('correos')) {
        return <Typography.Text>
          <strong>{t('order.fields.shipmentDataAvailable')}: </strong> {shipmentData?.tracking_id}
        </Typography.Text>
      } else if (shipmentData?.shipper_type?.toLowerCase().includes('ontime') || shipmentData?.shipper_type?.toLowerCase().includes('ctt') || shipmentData?.shipper_type?.toLowerCase().includes('dhl')) {
        return <>
          <Button ref={shipmentLink} icon={<Icons.InfoCircleOutlined />} onClick={() => openLink(shipmentData.tracking_url || '')}>
            {t('order.fields.shipmentDataAvailable')}
          </Button>
          <Typography.Text>
            <strong>{t('order.fields.shipmentDataAvailable')}: </strong> {shipmentData?.tracking_url}
          </Typography.Text>
        </>
      }
    }
    return null
  }

  const showWarehouseShipmentLink = () => {
    if (shipmentData?.tracking_url) {
      return <Space direction="vertical" size={8} style={{ width: '100%' }}>
        <Button ref={shipmentLink} icon={<Icons.InfoCircleOutlined />} onClick={() => openLink(shipmentData.tracking_url || '')}>
          {t('order.fields.shipmentDataAvailable')}
        </Button>
        <Typography.Text style={{ display: 'block', overflowWrap: 'anywhere' }}>
          <strong>{t('order.fields.shipmentDataAvailable')}: </strong>{shipmentData.tracking_url}
        </Typography.Text>
      </Space>
    }

    return shipmentData?.tracking_id
      ? <Typography.Text>{shipmentData.tracking_id}</Typography.Text>
      : t('order.fields.shipmentDataAvailable')
  }

  const updateAddressInfo = (addressData: any, addressType: String) => {
    if (order) {
      const orderToUpdate = order
      if (addressType === 'shipping') {
        orderToUpdate.shipping_address = addressData
        orderToUpdate.shipping_address_id = addressData.id
      } else {
        orderToUpdate.billing_address = addressData
        orderToUpdate.billing_address_id = addressData.id
      }

      setModifiedAddress(Date.now)
      setOrder(orderToUpdate)
    }
  }

  const Text: React.FC<{ title: string; children: ReactNode; }> = ({ title, children }) => {
    return (
      <Typography.Text>
        <span style={{ fontWeight: 'bold' }}>{`${t(title)}: `}</span>
        {children}
      </Typography.Text>
    )
  }

  const internalShipmentTotal = getInternalShipmentTotal(order?.user.orders)
  const supplementsTotal = order?.user.orders?.filter(o => o.status_id && ![ORDER_STATUS_ID_PENDING_PAYMENT, CANCELLED_STATUS, RETURN_STATUS].includes(o.status_id))
    .map(o => {
      const sups = o.supplements.filter(s => !s.deleted).filter(s => s.volumetric)
      return sups.reduce((acc, curr) => acc + (curr.supplement || 0), 0)
    })
    .reduce((acc, curr) => acc + curr, 0) || 0
  const insuranceTotal = order?.user.orders?.filter(o => o.status_id && ![ORDER_STATUS_ID_PENDING_PAYMENT, CANCELLED_STATUS, RETURN_STATUS].includes(o.status_id)).reduce((acc, curr) => acc + (curr.shipping_insurance_price || 0), 0) || 0
  const customOrdersTotal = order?.user.orders?.filter(o => o.status_id && ![ORDER_STATUS_ID_PENDING_PAYMENT, CANCELLED_STATUS, RETURN_STATUS].includes(o.status_id)).reduce((acc, curr) => acc + curr.custom_order_price, 0) || 0
  const internalTotal = internalShipmentTotal + supplementsTotal + insuranceTotal + customOrdersTotal

  const hasProcessedNote = !!order?.notes && order.notes.length > 0
  const hasEstimatedNote = !!order?.estimated_notes && order.estimated_notes.length > 0

  return (
    <Card
      bordered={false}
      style={props.warehouseMode ? { background: 'transparent', border: 'none', boxShadow: 'none', height: '100%', display: 'flex', flexDirection: 'column' } : { height: '100%' }}
      bodyStyle={props.warehouseMode ? { padding: 0, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 } : undefined}
    >
      <Space direction="vertical" style={props.warehouseMode ? { display: 'none' } : { width: '100%', height: '100%' }}>
        {props.comesFromIssue && (
          <Typography style={{ fontWeight: 600, fontSize: '18px', marginBottom: 8 }}>
            <Icons.UserOutlined style={{ color: '#c3fb00' }} />
            <span style={{ marginLeft: 8 }}>{t('issues.titles.client')}</span>
          </Typography>
        )}
        {!props.warehouseMode && <Space direction="horizontal" align="center">
          <Typography.Title
            level={3}
            style={{ cursor: 'pointer', color: 'darkblue', textAlign: 'left', width: 'fit-content', wordBreak: 'break-word', gap: '2rem', marginBottom: 0 }}
            onClick={() => show('users', order?.user_id || 0)}
          >
            {order?.user?.name}
          </Typography.Title>
            {(order?.subscribed_order && order?.shipping_method_id === ECONOMY_SHIPMENT_METHOD_ID) && <Tag style={{ background: '#c5f039', color: '#0e0460', border: 'none', marginLeft: 12, borderRadius: '5px' }}>
              {(order as any)?.subscriptions?.name}
            </Tag>}
        </Space>}
        {!props.warehouseMode && <Space
          direction="vertical"
          style={{
            width: '100%',
            textAlign: xl ? 'unset' : 'center'
          }}
        >
          <Row style={{ gap: 4, justifyContent: 'space-between' }}>
            <Col lg={24} xl={7} style={{ display: 'flex', flexDirection: 'column' }}>
              <Text title="users.email">
                {order?.user?.email}
              </Text>
              <Text title="users.created_at">
                <DateField format={DEFAULT_DATE_TIME_FORMAT} value={order?.user.created_at} />
              </Text>
              <Text title="users.birthday">
                {(order?.user.birthday) ? <DateField format={DEFAULT_DATE_FORMAT} value={order?.user.birthday} /> : t('common.no_specified')}
              </Text>
              <Text title="users.gender">
                {t(getGender(order?.user.gender))}
              </Text>
              <Text title="users.island">
                {order?.shipping_address?.island?.name || ''}
              </Text>
            </Col>
            <Col lg={24} xl={8} style={{ display: 'flex', flexDirection: 'column' }}>
              <Text title="users.orders">
                {order?.user.orders?.filter(o => o.status_id !== ORDER_STATUS_ID_PENDING_PAYMENT).length}
              </Text>
              <Text title="users.ordersInProgress">
                {order?.user.orders?.filter(o => o.status_id && [ORDER_STATUS_ID_REQUESTED, PROCESSED_STATUS, SUPPLEMENT_STATUS, ADMITTED_STATUS, PREPARED_STATUS, ORDER_STATUS_ID_ON_HOLD].includes(o.status_id)).length}
              </Text>
              <Text title="users.delivered_orders">
                {order?.user.orders?.filter(o => o.status_id === DELIVERED_STATUS).length}
              </Text>
              <Text title="users.cancelled_orders">
                {order?.user.orders?.filter(o => o.status_id === CANCELLED_STATUS).length}
              </Text>
              <Text title="users.totalReturns">
                {order?.user.orders?.filter(o => o.status_id === RETURN_STATUS).length}
              </Text>
              <Text title="users.partialReturns">
                {order?.user.orders?.filter(o => o.status_id === PARCIAL_RETURN_STATUS).length}
              </Text>
              <Text title="users.penalization_orders">
                {order?.user.orders?.filter(o => o.status_id === PENALIZATION_STATUS).length}
              </Text>
              <Text title="order.pendingPaymentOrders">
                {order?.user.orders?.filter(o => o.status_id === ORDER_STATUS_ID_PENDING_PAYMENT).length}
              </Text>
              <Text title="users.last_order_date">
                {order?.user?.orders && order?.user?.orders.length ? getLastOrder(order?.user?.orders, -1) : ''}
              </Text>
              <Text title="users.prev_last_order_date">
                {order?.user?.orders && order?.user?.orders.length ? getLastOrder(order?.user?.orders, -2) : ''}
              </Text>
            </Col>
            <Col lg={24} xl={7} style={{ display: 'flex', flexDirection: 'column' }}>
              <Text title="users.total">
                {currency(order?.user.orders?.filter(o => o.status_id && ![ORDER_STATUS_ID_PENDING_PAYMENT, CANCELLED_STATUS, RETURN_STATUS].includes(o.status_id)).reduce((acc, curr) => acc + curr.total, 0))}
              </Text>
              <Text title="users.internal_shipment_total">
                {currency(internalShipmentTotal)}
              </Text>
              <Text title="users.supplements_total">
                {currency(supplementsTotal)}
              </Text>
              <Text title="users.insurance_total">
                {currency(insuranceTotal)}
              </Text>
              <Text title="users.custom_orders_total">
                {currency(customOrdersTotal)}
              </Text>
              <Text title="users.internal_total">
                {currency(internalTotal)}
              </Text>
            </Col>
          </Row>
        </Space>}
      </Space>
      {props.comesFromIssue && (
        <Typography style={{ fontWeight: 600, fontSize: '18px', marginBottom: 8 }}>
          <Icons.EnvironmentOutlined style={{ color: '#c3fb00' }} />
          <span style={{ marginLeft: 8 }}>{t('issues.titles.addresses')}</span>
        </Typography>
      )}
      <Row>
        <Col xl={props.warehouseMode ? 24 : 12} lg={24} xs={24} id={String(modifiedAddress)}>
          {props.warehouseMode
            ? <Card size="small" style={{ width: '100%' }} bodyStyle={{ height: '100%' }}>
                <AddressInfo
                  orderId={order?.id}
                  address={(order?.shipping_address_id && order?.shipping_address)
                    ? { ...order?.shipping_address, id: Number(order?.shipping_address_id) }
                    : order?.shipping_address}
                  title={t('order.fields.shippingAddress')}
                  type={'shipping'}
                  updateAddressInfo={updateAddressInfo}
                  canEdit={props.canEdit}
                  nameExtra={
                    (order?.subscribed_order && order?.shipping_method_id === ECONOMY_SHIPMENT_METHOD_ID)
                      ? <Tag style={{ background: '#c5f039', color: '#0e0460', border: 'none', marginLeft: 8, borderRadius: '5px' }}>
                          {(order as any)?.subscriptions?.name}
                        </Tag>
                      : undefined
                  }
                />
              </Card>
            : <AddressInfo
                orderId={order?.id}
                address={(order?.shipping_address_id && order?.shipping_address)
                  ? { ...order?.shipping_address, id: Number(order?.shipping_address_id) }
                  : order?.shipping_address}
                title={t('order.fields.shippingAddress')}
                type={'shipping'}
                updateAddressInfo={updateAddressInfo}
                canEdit={props.canEdit}
              />}
        </Col>
        {!props.warehouseMode && <Col xl={12} lg={24} xs={24} id={String(modifiedAddress)}>
          <AddressInfo
            orderId={order?.id}
            address={(order?.billing_address_id && order?.billing_address)
              ? { ...order?.billing_address, id: Number(order?.billing_address_id) }
              : order?.billing_address}
            title={t('order.fields.billingAddress')}
            type={'billing'}
            updateAddressInfo={updateAddressInfo}
            canEdit={props.canEdit}
          />
        </Col>}
      </Row>
      <Row style={props.warehouseMode ? { flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' } : undefined}>
        <Col xl={24} lg={36} xs={36} id={String(modifiedAddress)} style={props.warehouseMode ? { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 } : undefined}>
          {!props.warehouseMode && <Space direction="vertical" style={{ width: '100%', marginTop: '30px' }}>
            {showShipmentData()}
          </Space>}
          {props.warehouseMode && <Card
            size="small"
            style={{ marginTop: 12, minHeight: 72, flex: '1 1 72px' }}
          >
            {showWarehouseShipmentLink()}
          </Card>}
          <Card
            size="small"
            bordered={props.warehouseMode}
            style={{ marginTop: props.warehouseMode ? 12 : 30, minHeight: props.warehouseMode ? 72 : undefined, flex: props.warehouseMode ? '1 1 72px' : undefined }}
          >
            <Space style={{ display: 'flex', justifyContent: 'left' }}>
              <Button
                onClick={async () => {
                  if (canGenerateInvoice && !order?.invoices?.invoice_number) {
                    window.open(`${API_URL}/billings/pdf/${order?.id}`, '_blank', 'noopener,noreferrer')
                  } else {
                    window.open(`${API_URL}/billings/show/${order?.id}`, '_blank', 'noopener,noreferrer')
                  }
                }}>
                {invoiceButtonAction}
              </Button>
              {invoiceWarning && (
                <Tooltip title={t('invoiceWarning')}>
                  <Icons.WarningOutlined style={{ color: 'orange' }} />
                </Tooltip>
              )}
            </Space>
            {(order?.invoices?.invoice_number) && <div style={{ position: 'relative' }}>
              <Collapse ghost expandIconPosition='end' style={{ width: '260px', margin: '12px 0' }}>
                <Collapse.Panel header={t('order.fields.see_billings_and_refunds')} key="1">
                  <ul style={{ paddingLeft: 20 }}>
                    <li style={{ listStyleType: 'disc' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '0.5rem 0', justifyContent: 'space-between' }}>
                        <Typography>
                          {t('order.fields.see_billing')}
                        </Typography>
                        <Button onClick={() => window.open(`${API_URL}/billings/show/${order?.id}`, '_blank', 'noopener,noreferrer')}>
                          <Icons.EyeOutlined style={{ display: 'flex', alignItems: 'center' }}/>
                        </Button>
                      </div>
                    </li>
                    {refundInvoices.map((refundInvoice) => (
                      <li key={refundInvoice.id} style={{ listStyleType: 'disc' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '0.5rem 0', justifyContent: 'space-between' }}>
                          <Typography>
                            {`${refundInvoice.type === 'correction' ? t('order.fields.see_correction') : t('order.fields.see_refund')} ${refundInvoice.invoice}`}
                          </Typography>
                          <Button onClick={() => window.open(`${API_URL}/refunds/show/${refundInvoice.invoice}`, '_blank', 'noopener,noreferrer')}>
                            <Icons.EyeOutlined style={{ display: 'flex', alignItems: 'center' }} />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </Collapse.Panel>
              </Collapse>
            </div>}
          </Card>
        </Col>
        {!props.warehouseMode && <Col xl={24} lg={36} xs={36} id='notes'>
          {hasProcessedNote
            ? (
                <Col xl={12} lg={24} xs={24} id='order-note'>
                  <div style={{ marginTop: '30px' }}>
                    <Typography.Title level={4} style={{ marginRight: '1%' }}>{t('order.fields.noteProcessed')}</Typography.Title>
                    {order.notes}
                  </div>
                </Col>
              )
            : null}
          {hasEstimatedNote
            ? (
                <Col xl={12} lg={24} xs={24} id='order-note'>
                  <div style={{ marginTop: '30px' }}>
                    <Typography.Title level={4} style={{ marginRight: '1%' }}>{t('order.fields.noteEstimated')}</Typography.Title>
                    {order.estimated_notes}
                  </div>
                </Col>
              )
            : null}
        </Col>}
      </Row>
    </Card>
  )
}
